import { calculateEnergyBackupSystemDesignTotalCost } from '@/components/Quotes/EnergyBackup.utils';
import { currencyFormatter } from '@/helpers/currencyFormatter';

import {
  AccountDocument,
  EnergyBackupSystem,
  EnergyBackupSystemDesign,
  EquipmentTypeLabels,
  // Site prep, accessories, and discounts are temporarily commented out for the generator program; may return.
  // GeneratorAccessoryItem,
  // GeneratorDiscountItem,
  GeneratorEquipmentItem,
  GeneratorEquipmentTypeLabels,
  // GeneratorSitePrepItem,
  GeneratorSystem,
  GeothermalIndoorUnitTypeLabels,
  GeothermalLoopTypeLabels,
  HVACAccessoryItem,
  HVACAccessoryLabels,
  HVACDiscountItem,
  HVACEquipmentItem,
  HVACEquipmentTypeLabels,
  HVACSitePrepItem,
  HVACSystem,
  HVACZoningItem,
  ProgramType,
  WaterHeaterDiscountItem,
  WaterHeaterEquipmentItem,
  WaterHeaterEquipmentTypeLabels,
  WaterHeaterSitePrepItem,
  WaterHeaterSystem,
} from 'types';

export const getSystemDesignProperties = (account: AccountDocument, systemDesign: any) => {
  switch (account?.programType) {
    case ProgramType.hvac: {
      const flattenedSystemDesign = systemDesign?.systems?.reduce(
        (acc: any, system: HVACSystem) => {
          acc.equipment.push(...system.equipment.items);
          acc.accessories.push(...(system?.accessories?.items || []));
          acc.zoning.push(...(system?.zoning?.items || []));
          acc.sitePrep.push(...(system?.sitePrep?.items || []));
          acc.discounts.push(...(system?.discounts?.items || []));
          return acc;
        },
        {
          equipment: [],
          accessories: [],
          zoning: [],
          sitePrep: [],
          discounts: [],
        } as any,
      );
      return [
        ...(flattenedSystemDesign?.equipment?.map((e: HVACEquipmentItem) => ({
          label: `${HVACEquipmentTypeLabels[e.type]} ${!systemDesign.isPreliminary ? `- ${e.name}` : ''}`,
          value: e.quantity,
        })) || []),
        ...(flattenedSystemDesign?.accessories?.map((i: HVACAccessoryItem) => ({
          label: `${HVACAccessoryLabels[i.type]} ${!systemDesign.isPreliminary ? `- ${i.name}` : ''}`,
          value: i.quantity,
        })) || []),
        ...(flattenedSystemDesign?.zoning?.map((s: HVACZoningItem) => ({
          label: `Zoning`,
          value: s.quantity,
        })) || []),
        ...(flattenedSystemDesign?.sitePrep?.map((s: HVACSitePrepItem) => ({
          label: `Site Prep - ${s.name}`,
          value: 1,
        })) || []),
        ...(flattenedSystemDesign?.discounts?.map((s: HVACDiscountItem) => ({
          label: `Discount - ${s.name}`,
          value: 1,
        })) || []),
      ];
    }
    case ProgramType.waterHeater: {
      const flattenedSystemDesign = systemDesign?.systems?.reduce(
        (acc: any, system: WaterHeaterSystem) => {
          acc.equipment.push(...system.equipment.items);
          acc.sitePrep.push(...(system?.sitePrep?.items || []));
          acc.discounts.push(...(system?.discounts?.items || []));
          return acc;
        },
        {
          equipment: [],
          accessories: [],
          zoning: [],
          sitePrep: [],
          discounts: [],
        } as any,
      );
      return [
        ...(flattenedSystemDesign?.equipment?.map((e: WaterHeaterEquipmentItem) => ({
          label: `${WaterHeaterEquipmentTypeLabels[e.type]} ${!systemDesign.isPreliminary && e.manufacturer && e.model ? `- ${e.manufacturer} ${e.model}` : ''}`,
          value: e.quantity,
        })) || []),
        ...(flattenedSystemDesign?.sitePrep?.map((s: WaterHeaterSitePrepItem) => ({
          label: `Site Prep - ${s.name}`,
          value: 1,
        })) || []),
        ...(flattenedSystemDesign?.discounts?.map((s: WaterHeaterDiscountItem) => ({
          label: `Discount - ${s.name}`,
          value: 1,
        })) || []),
      ];
    }
    case ProgramType.generator: {
      const flattenedSystemDesign = systemDesign?.systems?.reduce(
        (acc: any, system: GeneratorSystem) => {
          acc.equipment.push(...system.equipment.items);
          // Site prep, accessories, and discounts are temporarily commented out for the generator program; may return.
          // acc.sitePrep.push(...(system?.sitePrep?.items || []));
          // acc.accessories.push(...(system?.accessories?.items || []));
          // acc.discounts.push(...(system?.discounts?.items || []));
          return acc;
        },
        {
          equipment: [],
          accessories: [],
          sitePrep: [],
          discounts: [],
        } as any,
      );
      return [
        ...(flattenedSystemDesign?.equipment?.map((e: GeneratorEquipmentItem) => ({
          label: `${GeneratorEquipmentTypeLabels[e.type]} ${
            !systemDesign.isPreliminary && e.manufacturer && e.model ? `- ${e.manufacturer} ${e.model}` : ''
          }`,
          value: e.quantity,
        })) || []),
        // Site prep, accessories, and discounts are temporarily commented out for the generator program; may return.
        // ...(flattenedSystemDesign?.sitePrep?.map((s: GeneratorSitePrepItem) => ({
        //   label: `Site Prep - ${s.name}`,
        //   value: 1,
        // })) || []),
        // ...(flattenedSystemDesign?.accessories?.map((a: GeneratorAccessoryItem) => ({
        //   label: `Accessory - ${a.name}`,
        //   value: 1,
        // })) || []),
        // ...(flattenedSystemDesign?.discounts?.map((s: GeneratorDiscountItem) => ({
        //   label: `Discount - ${s.name}`,
        //   value: 1,
        // })) || []),
      ];
    }
    case ProgramType.energyBackup: {
      const equipment: EnergyBackupSystem[] = systemDesign?.equipment ?? [];
      if (!equipment.length) {
        return [];
      }
      // Install Package can split one model into several equipment items (e.g. per SKU), so merge rows
      // by label and sum quantities — PropertyListBox keys rows by label, so labels must be unique.
      const quantityByLabel = new Map<string, number>();
      for (const item of equipment) {
        for (const model of item.models ?? []) {
          const label = `${EquipmentTypeLabels[item.type] ?? item.type} - ${item.manufacturer} ${model.name}`;
          quantityByLabel.set(label, (quantityByLabel.get(label) ?? 0) + (model.quantity || 0));
        }
      }
      return [
        ...Array.from(quantityByLabel, ([label, value]) => ({ label, value })),
        {
          label: 'Total System Cost',
          value: currencyFormatter.format(
            calculateEnergyBackupSystemDesignTotalCost(systemDesign as EnergyBackupSystemDesign),
          ),
        },
      ];
    }
    case ProgramType.geothermal: {
      return [
        {
          label: 'Loop Type',
          value: GeothermalLoopTypeLabels[systemDesign?.loopType as keyof typeof GeothermalLoopTypeLabels] ?? '--',
        },
        {
          label: 'Indoor Unit Type',
          value:
            GeothermalIndoorUnitTypeLabels[
              systemDesign?.indoorUnitType as keyof typeof GeothermalIndoorUnitTypeLabels
            ] ?? '--',
        },
        { label: 'Capacity (Tons)', value: systemDesign?.capacityTons ?? '--' },
        { label: 'DHW Assist', value: systemDesign?.dhwAssist ? 'Yes' : 'No' },
        { label: 'Auxiliary Heat', value: systemDesign?.auxiliaryHeat ? 'Yes' : 'No' },
        ...(systemDesign?.auxiliaryHeat
          ? [{ label: 'Auxiliary Heat Type', value: systemDesign?.auxiliaryHeatType ?? '--' }]
          : []),
        {
          label: 'Total Lease Amount',
          value: systemDesign?.totalLeaseAmount ? currencyFormatter.format(systemDesign.totalLeaseAmount) : '--',
        },
        { label: 'Energy Community Eligible', value: systemDesign?.ecEligible ? 'Yes' : 'No' },
      ];
    }
    case ProgramType.solar:
    case ProgramType.doePr:
    case ProgramType.newCommunity:
    case ProgramType.newHomes:
    case ProgramType.consumerSolar: {
      const solarAccount = account as AccountDocument<
        ProgramType.doePr | ProgramType.solar | ProgramType.consumerSolar
      >;
      return [
        {
          label: 'System Size (kW)',
          value: systemDesign?.systemSizeKw ?? solarAccount?.systemDesign?.systemSizeKw ?? '--',
        },
        {
          label: 'Year-1 Production (kWh)',
          value:
            systemDesign?.systemFirstYearProductionKwh?.toFixed(0) ??
            solarAccount?.systemDesign?.systemFirstYearProductionKwh?.toFixed(0) ??
            '--',
        },
        {
          label: 'Panel Count',
          value:
            systemDesign?.totalPanelCount > 0
              ? systemDesign?.totalPanelCount
              : (solarAccount?.systemDesign?.totalPanelCount ?? '--'),
        },
        {
          label: 'Panel Manufacturer',
          value: systemDesign?.panelManufacturer ?? solarAccount?.systemDesign?.panelManufacturer ?? '--',
        },
        { label: 'Panel Model', value: systemDesign?.panelModel ?? solarAccount?.systemDesign?.panelModel ?? '--' },
        {
          label: 'Inverter Manufacturer',
          value:
            systemDesign?.inverters?.[0]?.manufacturer ?? solarAccount?.systemDesign?.inverterManufacturer ?? '--',
        },
        {
          label: 'Mounting Type',
          value: systemDesign?.mountingType ?? solarAccount?.systemDesign?.mountingType ?? '--',
        },
        {
          label: 'Hardware Manufacturer',
          value: systemDesign?.mountingManufacturer ?? solarAccount?.systemDesign?.mountingManufacturer ?? '--',
        },
      ];
    }
    default:
      return [];
  }
};
